import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { UserForm } from "./UserForm";
import { useClientRoleOptions, useRoleOptions } from "@/hooks/useEnumOptions";
import { UserUpdatePasswordModal } from "./UserUpdatePasswordModal";
import {
  UpdateUserSchema,
  UpdateUserClientSchema,
  UpdateUserFormData,
  UpdateUserPasswordFormData,
  UserForUpdate,
  buildDefaultValuesUpdateUser,
} from "@/types/forms/shared/user.form";

const FORM_ID = "update-user-form";

type UserUpdateModalProps = {
  user: UserForUpdate | null;
  open: boolean;
  onClose: () => void;
  updateUserMutation: {
    mutateAsync: (payload: { id: string; data: UpdateUserFormData }) => Promise<unknown>;
    isPending: boolean;
  };
  updatePasswordMutation: {
    mutateAsync: (payload: { id: string; data: UpdateUserPasswordFormData }) => Promise<unknown>;
    isPending: boolean;
  };
  isBackOffice?: boolean;
};

export const UserUpdateModal = ({
  user,
  open,
  onClose,
  updateUserMutation,
  updatePasswordMutation,
  isBackOffice = false,
}: UserUpdateModalProps) => {
  const { t } = useTranslation("shared");
  const { mutateAsync: updateUser, isPending } = updateUserMutation;
  const [passwordOpen, setPasswordOpen] = useState(false);

  const schema = isBackOffice ? UpdateUserSchema : UpdateUserClientSchema;
  const roleOptions = useRoleOptions();
  const clientRoleOptions = useClientRoleOptions();
  const rolesOptions = isBackOffice ? roleOptions : clientRoleOptions;

  const { control, handleSubmit, reset } = useForm<UpdateUserFormData>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: user ? buildDefaultValuesUpdateUser(user) : undefined,
  });

  useEffect(() => {
    if (open && user) reset(buildDefaultValuesUpdateUser(user));
  }, [open, user, reset]);

  useEffect(() => {
    if (!open) setPasswordOpen(false);
  }, [open]);

  const onSubmit: SubmitHandler<UpdateUserFormData> = async (data) => {
    if (!user) return;
    await updateUser({ id: user.id, data });
    onClose();
  };

  return (
    <>
      <Modal
        open={open && !passwordOpen}
        onClose={onClose}
        dismissible={!isPending}
        title={t("userUpdateModal.title")}
        footer={
          <Button type="submit" form={FORM_ID} disabled={isPending} className="w-full">
            {isPending ? <Loader2 className="animate-spin" /> : t("userUpdateModal.submit")}
          </Button>
        }
      >
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col gap-4">
          <UserForm control={control} rolesOptions={rolesOptions} />
          <Button
            type="button"
            variant="link"
            onClick={() => setPasswordOpen(true)}
            className="text-brand700 hover:text-brand950 w-full text-left"
          >
            {t("userUpdateModal.editPassword")}
          </Button>
        </form>
      </Modal>
      <UserUpdatePasswordModal
        user={user}
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        updatePasswordMutation={updatePasswordMutation}
      />
    </>
  );
};
